/**
 * Shared vocabulary for SSO access-denial reasons, mirroring
 * strategy_management/sso/access_denial.py on the backend.
 *
 * The backend attaches `code`, `detail`, `detail_en`, `detail_ar` to its 403
 * response. Any new code added here must also be added to the backend map and
 * to both locale files (accessDenied.*).
 */

export const ACCESS_DENIAL_CODES = [
  'citizen_blocked',
  'position_type_missing',
  'account_inactive',
  'no_role',
  'not_authenticated',
  'no_system_access',
] as const;

export type AccessDenialCode = (typeof ACCESS_DENIAL_CODES)[number];

export interface AccessDenial {
  code: AccessDenialCode;
  detail?: string;
  detail_en?: string;
  detail_ar?: string;
  user?: Record<string, any>;
}

export function isAccessDenialCode(value: unknown): value is AccessDenialCode {
  return (
    typeof value === 'string' &&
    (ACCESS_DENIAL_CODES as readonly string[]).includes(value)
  );
}

/**
 * Extract an AccessDenial payload from an axios error response body.
 * Returns null if the body doesn't carry a known denial code.
 */
export function parseAccessDenial(body: unknown): AccessDenial | null {
  if (!body || typeof body !== 'object') return null;
  const data = body as Record<string, unknown>;
  const code = data.code;
  if (!isAccessDenialCode(code)) return null;
  return {
    code,
    detail: typeof data.detail === 'string' ? data.detail : undefined,
    detail_en: typeof data.detail_en === 'string' ? data.detail_en : undefined,
    detail_ar: typeof data.detail_ar === 'string' ? data.detail_ar : undefined,
    user: data.user && typeof data.user === 'object' ? data.user as Record<string, any> : undefined,
  };
}

export const ACCESS_DENIED_EVENT = 'auth:access-denied';
